import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Hero from "../components/Hero";
import Stats from "../components/Stats";
import ServiceCard from "../components/ServiceCard";
import {
  faHeadphones,
  faWind,
  faEye,
  faCalendarCheck,
  faPhone,
  faGraduationCap,
  faUserDoctor,
  faUsers,
  faHeart,
  faEnvelope,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import "./HomePage.css";

const HomePage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Scroll alla sezione ancorata se presente nell'URL
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  const stats = [
    { value: "10+", label: "Anni di esperienza" },
    { value: "5k+", label: "Pazienti visitati" },
    { value: "100%", label: "Soddisfazione" },
  ];

  const values = [
    {
      icon: faGraduationCap,
      title: "Formazione Eccellente",
      description:
        "Laurea in Medicina e Chirurgia, con specializzazione in Otorinolaringoiatria presso Centri di riferimento per le patologie di orecchio, naso e gola.",
    },
    {
      icon: faUserDoctor,
      title: "Aggiornamento Continuo",
      description:
        "Partecipazione costante a congressi nazionali e internazionali.",
    },
    {
      icon: faUsers,
      title: "Approccio Umano",
      description:
        "Ogni paziente è unico, merita ascolto e cure personalizzate.",
    },
    {
      icon: faHeart,
      title: "Passione e Dedizione",
      description: "La salute dei miei pazienti è la mia priorità assoluta.",
    },
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <Hero
        name="Centro di Otorinolaringoiatria Mattina"
        description="Da oltre 10 anni ci dedichiamo alla cura delle patologie di orecchio, naso e gola, con un approccio umano e tecnologicamente avanzato."
        image={require("../assets/photos/main-photo.jpg")}
      />

      {/* Stats Section */}
      <Stats stats={stats} />

      {/* Services Section */}
      <section className="services-section">
        <div className="services-container">
          <p className="services-label">I NOSTRI SERVIZI</p>
          <p className="services-description">
            Offriamo una gamma completa di servizi otorinolaringoiatrici,
            utilizzando tecnologie moderne per garantire diagnosi precise e
            trattamenti efficaci.
          </p>

          <div className="services-grid">
            <ServiceCard
              icon={faHeadphones}
              title="Patologie dell'Orecchio"
              description="Diagnosi e trattamento di otiti, ipoacusia, acufeni, vertigini e disturbi dell'equilibrio."
              linkTo="/servizi#orecchio"
            />
            <ServiceCard
              icon={faWind}
              title="Patologie del Naso"
              description="Trattamento di sinusiti, riniti, deviazione del setto, polipi nasale e disturbi respiratori."
              linkTo="/servizi#naso"
            />
            <ServiceCard
              icon={faEye}
              title="Patologie della Gola"
              description="Trattamento di faringiti, tonsilliti, disfonia, disturbi della deglutizione e russamento."
              linkTo="/servizi#gola"
            />
          </div>

          <div className="services-cta">
            <Link to="/servizi" className="btn-outline">
              Scopri di più
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview-section">
        <div className="about-preview-container">
          <div className="about-preview-content">
            <p className="about-preview-label">CHI SIAMO</p>
            <p className="about-preview-text">
              Siamo specializzati in Otorinolaringoiatria con oltre 10 anni di
              esperienza nella diagnosi e nel trattamento delle patologie
              dell'orecchio, del naso e della gola.
            </p>
            <p className="about-preview-text">
              Il nostro approccio combina competenze mediche avanzate con una
              profonda attenzione al rapporto umano con il paziente. Crediamo
              fermamente che l'ascolto e la comprensione siano fondamentali per
              un percorso di cura efficace.
            </p>

            <div className="about-values">
              {values.map((value, index) => (
                <div key={index} className="value-item">
                  <div className="value-icon">
                    <FontAwesomeIcon icon={value.icon} />
                  </div>
                  <div className="value-content">
                    <h4 className="value-title">{value.title}</h4>
                    <p className="value-description">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link to="/chi-sono" className="btn-link">
              Scopri di Più su di Noi
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="prenota" className="contact-cta-section">
        <div className="contact-cta-overlay">
          <div className="contact-cta-container">
            <p className="contact-cta-label">CONTATTACI</p>
            <h2 className="contact-cta-title">Prenota la Tua Visita Oggi</h2>
            <p className="contact-cta-description">
              Il primo passo verso la salute è una visita specialistica.
              Contattaci per fissare un appuntamento o per qualsiasi
              informazione.
            </p>

            <div className="contact-cta-buttons">
              <Link to="/contatti" className="btn-cta-primary">
                <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                Prenota Online
              </Link>
              <a href="tel:+393701627395" className="btn-cta-secondary">
                <FontAwesomeIcon icon={faPhone} className="me-2" />
                Chiama Ora
              </a>
            </div>

            <div className="contact-info-cards">
              <div className="contact-info-card">
                <FontAwesomeIcon icon={faPhone} className="contact-info-icon" />
                <div>
                  <div className="contact-info-label">Telefono</div>
                  <div className="contact-info-value phone-number">
                    +39 370 162 7395
                  </div>
                  <div className="contact-info-value phone-number">
                    +39 091 766 9253
                  </div>
                  <div>
                    <a
                      href="tel:+393701627395"
                      className="contact-info-link"
                      style={{ display: "block", marginBottom: "0.25rem" }}
                    >
                      Chiama cellulare →
                    </a>
                    <a
                      href="tel:+390917669253"
                      className="contact-info-link"
                      style={{ display: "block" }}
                    >
                      Chiama fisso →
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-info-card">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="contact-info-icon"
                />
                <div>
                  <div className="contact-info-label">Email</div>
                  <div className="contact-info-value">
                    centrodiotorino.mattina@gmail.com
                  </div>
                  <a
                    href="mailto:centrodiotorino.mattina@gmail.com"
                    className="contact-info-link"
                  >
                    Scrivi email →
                  </a>
                </div>
              </div>

              <div className="contact-info-card">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="contact-info-icon"
                />
                <div>
                  <div className="contact-info-label">Studio</div>
                  <div className="contact-info-value">
                    Via Capo dell'Acqua 6 (Piazza Duomo), Partinico
                  </div>
                  <a
                    href="https://maps.google.com/?q=Via+Capo+dell'Acqua+6,+Piazza+Duomo,+Partinico"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-info-link"
                  >
                    Vedi mappa →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
